import {
	getProduct,
	addToCartMultiple,
	cartOperationManager
} from "./_global-functions.js";

if (customElements.get("dynamic-atc") === undefined) {
	class DynamicATC extends HTMLElement {
		constructor() {
			super();

			this.product;
			this.productHandle;
			this.variantToAdd;
			this.addButton;
			this.componentId = Math.random().toString(36).substr(2, 9); // Unique ID for this component
		}

		async connectedCallback() {
			this.productHandle = this.dataset.productHandle;
			this.product = await getProduct(this.productHandle);

			if (!this.product) {
				console.error(`Product ${this.productHandle} not found`);
				return;
			}

			this.render();
		}

		render() {
			let content = 'Quick Add';
			// create button
			const button = document.createElement('div');
			button.classList.add('btn--ice');
			button.classList.add('dynamic-atc');
			button.innerHTML = content;
			this.appendChild(button);

			const variants = this.product.variants.filter(variant => { if(variant.price !== 0) return variant });
			this.variantToAdd = variants[0].id;

			// Always add click handler for Quick Add button
			button.addEventListener('click', () => {
				if (variants.length > 1) {
					// Show variant selector if product has multiple variants
					this.showVariantSelector();
				} else {
					// Add to cart directly if only one variant
					this.addToCart(this.variantToAdd);
				}
			});
		}

		setVariantSelectorOptions() {
			const variantSelectors = this.querySelectorAll('.variant-selector:not(.variant-selector--hidden)');
			variantSelectors.forEach(variantSelector => {
				variantSelector.addEventListener('click', (e) => {
					e.stopPropagation();
					const options = variantSelector.querySelector('.variant-selector__options');
					options.classList.toggle('variant-selector__options--open');
				});

				const optionElements = variantSelector.querySelectorAll('.variant-selector__option');
				optionElements.forEach(option => {
					option.addEventListener('click', (e) => {
						if(e.target.closest('.variant-selector__option--disabled')) return;

						e.stopPropagation();
						option.querySelector('input').checked = true;
						this.changeVariant(option);
						variantSelector.querySelector('.variant-selector__options')
							.classList.remove('variant-selector__options--open');
					});
				});
			});

			// Close dropdowns when clicking outside - scoped to this component only
			this.handleDocumentClick = (e) => {
				// Only handle clicks for THIS component's elements
				if (!this.contains(e.target)) {
					variantSelectors.forEach(variantSelector => {
						variantSelector.querySelector('.variant-selector__options')
							.classList.remove('variant-selector__options--open');
					});
				}
			};
			
			// Remove any existing listener before adding new one
			document.removeEventListener('click', this.handleDocumentClick);
			document.addEventListener('click', this.handleDocumentClick);
		}

		changeVariant(option) {
			
			// search for variant in the product.variants array
			const input = option.querySelector('input');
			const value = input.value;
			const parentLabel = option.closest('.variant-selector').querySelector('.variant-selector__label');
			parentLabel.textContent = value;

			// Update available options for other selectors
			this.updateAvailableOptions();
			this.checkAddButton();
		}

		updateAvailableOptions() {
			const selectedValues = this.getSelectedValues();
			
			const variantSelectors = this.querySelectorAll('.variant-selector');
			
			variantSelectors.forEach((selector, selectorIndex) => {
				const options = selector.querySelectorAll('.variant-selector__option');
				const optionName = this.product.options[selectorIndex].name;
				
				options.forEach(optionElement => {
					const input = optionElement.querySelector('input');
					const value = input.value;
					
					// Check if this option value creates a valid combination
					const testValues = [...selectedValues];
					testValues[selectorIndex] = value;
					
					const isAvailable = this.isVariantCombinationAvailable(testValues);
					
					// Enable/disable the option
					input.disabled = !isAvailable;
					optionElement.classList.toggle('variant-selector__option--disabled', !isAvailable);
					
					// If currently selected option becomes unavailable, uncheck it
					if (!isAvailable && input.checked) {
						input.checked = false;
						const label = selector.querySelector('.variant-selector__label');
						label.textContent = this.product.options[selectorIndex].name;
					}
				});
			});
		}

		getSelectedValues() {
			const values = [];
			const variantSelectors = this.querySelectorAll('.variant-selector');
			
			variantSelectors.forEach((selector, index) => {
				const checkedInput = selector.querySelector('input:checked');
				values[index] = checkedInput ? checkedInput.value : null;
			});
			
			return values;
		}

		isVariantCombinationAvailable(testValues) {
			// Find variants that match the test combination (ignoring null values)
			const matchingVariants = this.product.variants.filter(variant => {
				return variant.options.every((option, index) => {
					return testValues[index] === null || testValues[index] === option;
				});
			});
			
			// Check if any matching variant is available
			return matchingVariants.some(variant => variant.available);
		}

		getAvailableValuesForOption(optionIndex, currentSelections) {
			const availableValues = new Set();
			
			this.product.variants.forEach(variant => {
				if (!variant.available) return;
				
				// Check if this variant matches current selections for other options
				const matches = variant.options.every((option, index) => {
					if (index === optionIndex) return true; // We're checking this option
					return currentSelections[index] === null || currentSelections[index] === option;
				});
				
				if (matches) {
					availableValues.add(variant.options[optionIndex]);
				}
			});
			
			return Array.from(availableValues);
		}

		checkAddButton() {
			const activeVariants = this.querySelectorAll('input:checked');
			const activeVariantsValues = Array.from(activeVariants).map(input => input.value);

			const productToAdd = this.product.variants.filter(variant => {
				return variant.options.every(option => activeVariantsValues.includes(option)) && variant.available;
			});

			if (productToAdd.length > 0) {
				this.variantToAdd = productToAdd[0].id;
				this.addButton.disabled = false;
			} else {
				this.addButton.disabled = true;
			}
		}

		showVariantSelector() {
			// Replace the Quick Add button with variant selectors
			const container = this.querySelector('.dynamic-atc');
			container.innerHTML = ''; // Clear the button
			container.classList.remove('btn--ice'); // Remove button styling
			
			this.buildVariantSelector();
			this.setVariantSelectorOptions();
		}

		buildVariantSelector() {
			let variants = `<div class="variant-selectors">`;

			this.product.options.forEach((option, optionIndex) => {
				// Get available values for this option initially (with no selections)
				const availableValues = this.getAvailableValuesForOption(optionIndex, new Array(this.product.options.length).fill(null));
				
				// Check if this option has only one available value
				let hasOnlyOneOption = availableValues.length === 1;

				if(!hasOnlyOneOption) {
					const hasFreeVariant = this.product.variants.some(variant => variant.price === 0);
					if(hasFreeVariant) {
						hasOnlyOneOption = true;
					}
				}

				// Only show available values
				const optionElements = option.values
					.filter(value => availableValues.includes(value))
					.map((value, index) => {
						// Check if this is a color option to add swatch
						const isColorOption = option.name.toLowerCase() === 'color';
						const colorSwatch = isColorOption ? `<span class="color-swatch" style="background-color: ${value.toLowerCase()};"></span>` : '';
						
						return `
							<div class="variant-selector__option">
								<input type="radio" 
									name="${this.componentId}-${option.name}" 
									value="${value}" 
									id="${this.componentId}-${option.name}-${value}"
									${hasOnlyOneOption ? 'checked' : ''}>
								<label for="${this.componentId}-${option.name}-${value}">
									${this.product.handle.includes('baggu') ? '' : colorSwatch }${value}
								</label>
							</div>
						`;
					}).join('');

				// Only show the variant selector if there's more than one option
				if (!hasOnlyOneOption) {
					variants += `
						<div class="variant-selector btn--ice">
							<p class="variant-selector__label">${option.name}</p>
							<span class="variant-selector__arrow">
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
									<path d="M11.2861 1.59863L6 6.79785L0.712891 1.59863L1.11816 1.20117L5.64941 5.65723L6 6.00195L6.35059 5.65723L10.8809 1.20117L11.2861 1.59863Z" fill="#75787B" stroke="black"/>
								</svg>
							</span>
							<div class="variant-selector__options">
								${optionElements}
							</div>
						</div>
					`;
				} else {
					// Hide the selector but keep the input for form processing
					variants += `
						<div class="variant-selector variant-selector--hidden" style="display: none;">
							<div class="variant-selector__options">
								${optionElements}
							</div>
						</div>
					`;
				}
			})

			variants += `
				<button class="variant-atc btn--ice">Add to Cart</button>
			`;
			variants += `</div>`;

			const container = this.querySelector('.dynamic-atc');
			container.innerHTML = variants;

			// add event listener to the add button
			this.addButton = this.querySelector('.variant-atc');
			this.addButton.disabled = true;
			this.addButton.addEventListener('click', () => {
				this.addToCart(this.variantToAdd);
			});

			// Initial update to set correct availability states and check add button
			this.updateAvailableOptions();
			this.checkAddButton();
		}

	async addToCart(variant_id = this.variantToAdd.id) {
		// Check if cart operation is in progress
		if (cartOperationManager.isInProgress()) {
			return;
		}

		try {
			const response = await addToCartMultiple({ items: [{ id: variant_id, quantity: 1 }] });

			// Dispatch scroll event after successful add to cart
			document.dispatchEvent(new CustomEvent('cart-slider-scroll-to-top', {
				detail: { 
					source: 'dynamic-atc',
					productHandle: this.productHandle 
				}
			}));

		} catch (error) {
			console.error(`Error adding to cart: ${this.productHandle}`, error);
		}
	}

		disconnectedCallback() {
			// Clean up document listener when component is removed
			if (this.handleDocumentClick) {
				document.removeEventListener('click', this.handleDocumentClick);
			}
		}
	}

	customElements.define("dynamic-atc", DynamicATC);
}
